import { computed, nextTick, onBeforeUnmount, onMounted, ref } from "vue";
import { cloneDeep } from "lodash-es";
import { useNuxtApp } from "#app";
import { resolveResponsiveViewportWidth } from "@/utils/responsiveViewport";
import {
  getRequestUserAgent,
  getViewportWidthByUserAgent,
  shouldUseUserAgentViewport,
} from "@/utils/ssrDevice";

const DESKTOP_MIN_WIDTH = 1200;
const TABLET_MIN_WIDTH = 768;
const WIDE_MIN_WIDTH = 1600;

function getWindowWidth() {
  if (typeof window === "undefined") {
    return DESKTOP_MIN_WIDTH;
  }
  return resolveResponsiveViewportWidth({
    templateKey: document.body?.getAttribute("data-template-key"),
    visualWidth: window.visualViewport?.width,
    innerWidth: window.innerWidth,
    clientWidth: document.documentElement?.clientWidth,
  });
}

function resolveBreakpointByWidth(width) {
  if (width >= WIDE_MIN_WIDTH) {
    return "wide";
  }
  if (width >= DESKTOP_MIN_WIDTH) {
    return "desktop";
  }
  if (width >= TABLET_MIN_WIDTH) {
    return "tablet";
  }
  return "mobile";
}

function mergeResponsiveRect(baseValue, patchValue) {
  if (!patchValue) {
    return baseValue;
  }
  return {
    ...(baseValue || {}),
    ...patchValue,
  };
}

function hasConfigValue(value) {
  return value !== undefined && value !== null && value !== "";
}

function resolveSpacingValue(value) {
  if (value === "auto") {
    return "auto";
  }
  if (typeof value === "number") {
    return `${value}px`;
  }
  return "0px";
}

function buildBorderStyle(borderConfig) {
  if (!borderConfig?.enabled) {
    return {};
  }
  const width = typeof borderConfig.width === "number" ? borderConfig.width : 1;
  const style = borderConfig.style || "solid";
  const color = borderConfig.color || "rgba(226,191,176,0.3)";
  return {
    border: `${width}px ${style} ${color}`,
  };
}

function buildShadowStyle(shadowConfig, fallbackShadowEnabled) {
  if (shadowConfig?.enabled) {
    const offsetX = typeof shadowConfig.offsetX === "number" ? shadowConfig.offsetX : 0;
    const offsetY = typeof shadowConfig.offsetY === "number" ? shadowConfig.offsetY : 8;
    const blur = typeof shadowConfig.blur === "number" ? shadowConfig.blur : 24;
    const spread = typeof shadowConfig.spread === "number" ? shadowConfig.spread : 0;
    const color = shadowConfig.color || "rgba(0, 0, 0, 0.12)";
    const inset = shadowConfig.inset ? " inset" : "";
    return {
      boxShadow: `${offsetX}px ${offsetY}px ${blur}px ${spread}px ${color}${inset}`,
    };
  }
  if (fallbackShadowEnabled) {
    return {
      boxShadow: "0 8px 24px 0 rgba(0, 0, 0, 0.12)",
    };
  }
  return {};
}

function mergeResponsiveConfig(baseConfig, patchConfig) {
  if (!patchConfig) {
    return baseConfig;
  }
  const nextConfig = {
    ...baseConfig,
    ...patchConfig,
  };

  if (baseConfig?.padding || patchConfig?.padding) {
    nextConfig.padding = mergeResponsiveRect(baseConfig?.padding, patchConfig?.padding);
  }
  if (baseConfig?.margin || patchConfig?.margin) {
    nextConfig.margin = mergeResponsiveRect(baseConfig?.margin, patchConfig?.margin);
  }
  if (baseConfig?.radius || patchConfig?.radius) {
    nextConfig.radius = mergeResponsiveRect(baseConfig?.radius, patchConfig?.radius);
  }
  if (baseConfig?.positionVal || patchConfig?.positionVal) {
    nextConfig.positionVal = {
      ...(baseConfig?.positionVal || {}),
      ...(patchConfig?.positionVal || {}),
    };
  }

  return nextConfig;
}

function setByPath(target, path, value) {
  if (!target || !path) {
    return;
  }
  const keys = String(path).split(".");
  let current = target;
  keys.forEach((key, index) => {
    const isLast = index === keys.length - 1;
    if (isLast) {
      current[key] = value;
      return;
    }
    if (
      current[key] === undefined
      || current[key] === null
      || typeof current[key] !== "object"
    ) {
      current[key] = /^\d+$/.test(keys[index + 1]) ? [] : {};
    }
    current = current[key];
  });
}

function applyResponsiveOverrideMap(targetConfig, overrideMap) {
  if (!targetConfig || !overrideMap || typeof overrideMap !== "object") {
    return;
  }

  // Apply parent paths first so a more specific path such as
  // imageConfigs.0.url is not overwritten by a later imageConfigs array.
  Object.entries(overrideMap)
    .sort(([leftPath], [rightPath]) => {
      return leftPath.split(".").length - rightPath.split(".").length;
    })
    .forEach(([path, value]) => {
      setByPath(targetConfig, path, cloneDeep(value));
    });
}

export function useResponsiveConfig(configSource) {
  const nuxtApp = useNuxtApp();
  const shouldUseUserAgentWidth = shouldUseUserAgentViewport(nuxtApp);
  const windowWidth = ref(
    shouldUseUserAgentWidth
      ? getViewportWidthByUserAgent(getRequestUserAgent(nuxtApp))
      : getWindowWidth(),
  );

  const updateWindowWidth = () => {
    windowWidth.value = getWindowWidth();
  };

  onMounted(() => {
    if (typeof window !== "undefined") {
      updateWindowWidth();
      nextTick(() => {
        updateWindowWidth();
      });
      window.requestAnimationFrame(() => {
        updateWindowWidth();
      });
      window.addEventListener("resize", updateWindowWidth);
    }
  });

  onBeforeUnmount(() => {
    if (typeof window !== "undefined") {
      window.removeEventListener("resize", updateWindowWidth);
    }
  });

  const currentBreakpoint = computed(() => {
    return resolveBreakpointByWidth(windowWidth.value);
  });

  const resolvedConfig = computed(() => {
    const config = typeof configSource === "function" ? configSource() : configSource;
    if (!config) {
      return config;
    }

    const responsive = config.responsive;
    if (!responsive?.enabled) {
      return config;
    }

    const breakpoint = currentBreakpoint.value;
    const patch = responsive?.[breakpoint];
    const overridePatch =
      responsive?.enabled ? responsive?.[breakpoint]?.overrides : null;
    if (!patch && !overridePatch) {
      return config;
    }

    const mergedConfig = patch
      ? mergeResponsiveConfig(cloneDeep(config), patch)
      : cloneDeep(config);
    if (patch?.hidden) {
      mergedConfig.__responsiveHidden = true;
    }
    applyResponsiveOverrideMap(mergedConfig, overridePatch);
    return mergedConfig;
  });

  return {
    currentBreakpoint,
    resolvedConfig,
  };
}

export function resolveResponsiveSize(config, direction = "width") {
  if (!config) {
    return undefined;
  }
  const modeKey = direction === "width" ? "widthMode" : "heightMode";
  const valueKey = direction === "width" ? "width" : "height";
  const mode = config?.[modeKey];
  const value = config?.[valueKey];

  if (mode === "fill") {
    return "100%";
  }
  if (
    mode === "auto"
    || mode === "contentAuto"
    || mode === "imageAuto"
    || typeof value === "undefined"
    || value === null
    || value === ""
  ) {
    return "auto";
  }
  if (mode === "percent") {
    return `${value}%`;
  }
  if (typeof value === "number") {
    return `${value}px`;
  }
  return value;
}

export function buildCommonBoxStyle(
  config,
  bindDataStyle = {},
  options = {},
) {
  if (!config) {
    return { ...bindDataStyle };
  }

  const {
    includePosition = false,
    includeOpacity = true,
    includeZIndex = true,
  } = options;

  const width = resolveResponsiveSize(config, "width");
  const height = resolveResponsiveSize(config, "height");
  const overflowHiddenStyle = config.overflowHidden
    ? { overflow: "hidden", overflowX: "hidden", overflowY: "hidden" }
    : {};
  const borderStyle = buildBorderStyle(config.borderConfig);
  const shadowStyle = buildShadowStyle(config.shadowConfig, config.shadow);

  return {
    display: config.display || undefined,
    opacity:
      includeOpacity
      && typeof config.opacity === "number"
      && config.opacity >= 0
        ? String(config.opacity)
        : undefined,
    zIndex:
      includeZIndex && typeof config.zIndex === "number"
        ? String(config.zIndex)
        : undefined,
    backgroundColor: config.backgroundColor || undefined,
    backgroundImage: config.backgroundImage
      ? `url(${config.backgroundImage})`
      : undefined,
    backgroundSize: config.backgroundSize || undefined,
    backgroundRepeat: config.backgroundRepeat || undefined,
    backgroundPosition: config.backgroundPosition || undefined,
    paddingTop: resolveSpacingValue(config.padding?.top),
    paddingBottom: resolveSpacingValue(config.padding?.bottom),
    paddingLeft: resolveSpacingValue(config.padding?.left),
    paddingRight: resolveSpacingValue(config.padding?.right),
    marginTop: resolveSpacingValue(config.margin?.top),
    marginBottom: resolveSpacingValue(config.margin?.bottom),
    marginLeft: resolveSpacingValue(config.margin?.left),
    marginRight: resolveSpacingValue(config.margin?.right),
    borderTopLeftRadius: config.radius ? `${config.radius.leftTop}px` : "0",
    borderTopRightRadius: config.radius ? `${config.radius.rightTop}px` : "0",
    borderBottomLeftRadius: config.radius ? `${config.radius.leftBottom}px` : "0",
    borderBottomRightRadius: config.radius ? `${config.radius.rightBottom}px` : "0",
    width,
    height,
    minWidth:
      typeof config.minWidth === "number" ? `${config.minWidth}px` : undefined,
    maxWidth:
      typeof config.maxWidth === "number" ? `${config.maxWidth}px` : undefined,
    minHeight:
      typeof config.minHeight === "number" ? `${config.minHeight}px` : undefined,
    maxHeight:
      typeof config.maxHeight === "number" ? `${config.maxHeight}px` : undefined,
    position:
      includePosition && config.position && config.position !== "static"
        ? config.position
        : undefined,
    left:
      includePosition
      && hasConfigValue(config.positionVal?.left)
      && config.positionVal.left !== "auto"
        ? `${config.positionVal.left}px`
        : undefined,
    top:
      includePosition
      && hasConfigValue(config.positionVal?.top)
      && config.positionVal.top !== "auto"
        ? `${config.positionVal.top}px`
        : undefined,
    right:
      includePosition
      && hasConfigValue(config.positionVal?.right)
      && config.positionVal.right !== "auto"
        ? `${config.positionVal.right}px`
        : undefined,
    bottom:
      includePosition
      && hasConfigValue(config.positionVal?.bottom)
      && config.positionVal.bottom !== "auto"
        ? `${config.positionVal.bottom}px`
        : undefined,
    ...borderStyle,
    ...shadowStyle,
    ...bindDataStyle,
    ...overflowHiddenStyle,
  };
}
